import {
  Position,
  getConnectedEdges,
  getIncomers,
  getOutgoers,
  MarkerType
} from 'reactflow';
import dagre from '@dagrejs/dagre';
import produce from 'immer';
import type {
  Edge,
  // InputVar,
  Node,
  // ToolWithProvider,
  // ValueSelector,
} from './types';

import {
  BlockEnum,
  // ErrorHandleMode,
  NodeRunningStatus,
} from './types';
import type { IterationNodeType, LoopNodeType } from './nodes/types';

import { cloneDeep, groupBy, isEqual, uniqBy } from 'lodash-es';
// import xmljs from 'xml-js';
import { XMLParser, XMLBuilder } from 'fast-xml-parser'
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from "@/script/runAsync.mjs";
import { v4 as uuid4 } from 'uuid';
import parseAiBf from './parseAiBf.mjs';

import {
  CUSTOM_NODE,
  // DEFAULT_RETRY_INTERVAL,
  // DEFAULT_RETRY_MAX,
  // ITERATION_CHILDREN_Z_INDEX,
  // ITERATION_NODE_Z_INDEX,
  // LOOP_CHILDREN_Z_INDEX,
  // LOOP_NODE_Z_INDEX,
  NODE_WIDTH_X_OFFSET,
  START_INITIAL_POSITION,
} from './constants';

const WHITE = 'WHITE';
const GRAY = 'GRAY';
const BLACK = 'BLACK';

type ParallelInfoItem = {
  parallelNodeId: string;
  depth: number;
  isBranch?: boolean;
};
type NodeParallelInfo = {
  parallelNodeId: string;
  edgeHandleId: string;
  depth: number;
};
type NodeHandle = {
  node: Node;
  handle: string;
};
type NodeStreamInfo = {
  upstreamNodes: Set<string>;
  downstreamEdges: Set<string>;
};

/** @name 判断是否mac系统 */
export const isMac = () => {
  return navigator.userAgent.toUpperCase().includes('MAC');
};

/** @name 键盘按键名称映射表 */
const specialKeysNameMap: Record<string, string | undefined> = {
  ctrl: '⌘',
  alt: '⌥',
  shift: '⇧',
};

/** * @name 获取键盘按键名称 */
export const getKeyboardKeyNameBySystem = (key: string) => {
  if (isMac()) return specialKeysNameMap[key] || key;

  return key;
};

export const preprocessNodesAndEdges = (nodes: Node[], edges: Edge[]) => {
  return {
    nodes,
    edges,
  };
};

const isCyclicUtil = (
  nodeId: string,
  color: Record<string, string>,
  adjList: Record<string, string[]>,
  stack: string[],
) => {
  color[nodeId] = GRAY;
  stack.push(nodeId);

  for (let i = 0; i < adjList[nodeId].length; ++i) {
    const childId = adjList[nodeId][i];

    if (color[childId] === GRAY) {
      stack.push(childId);
      return true;
    }
    if (
      color[childId] === WHITE &&
      isCyclicUtil(childId, color, adjList, stack)
    )
      return true;
  }
  color[nodeId] = BLACK;
  if (stack.length > 0 && stack[stack.length - 1] === nodeId) stack.pop();
  return false;
};

const getCycleEdges = (nodes: Node[], edges: Edge[]) => {
  const adjList: Record<string, string[]> = {};
  const color: Record<string, string> = {};
  const stack: string[] = [];

  for (const node of nodes) {
    color[node.id] = WHITE;
    adjList[node.id] = [];
  }

  for (const edge of edges) adjList[edge.source]?.push(edge.target);

  for (let i = 0; i < nodes.length; i++) {
    if (color[nodes[i].id] === WHITE)
      isCyclicUtil(nodes[i].id, color, adjList, stack);
  }

  const cycleEdges = [];
  if (stack.length > 0) {
    const cycleNodes = new Set(stack);
    for (const edge of edges) {
      if (cycleNodes.has(edge.source) && cycleNodes.has(edge.target))
        cycleEdges.push(edge);
    }
  }

  return cycleEdges;
};

/** @name 初始化节点数据信息 */
export function generateNewNode({
  data,
  position,
  id,
  zIndex,
  type,
  ...rest
}: Omit<Node, 'id'> & { id?: string }): {
  newNode: Node;
  newIterationStartNode?: Node;
  newLoopStartNode?: Node;
} {
  const newNode = {
    id: id || `${Date.now()}`,
    type: type || CUSTOM_NODE,
    data,
    position,
    targetPosition: Position.Left,
    sourcePosition: Position.Right,
    // zIndex:
    //   data.type === BlockEnum.Iteration
    //     ? ITERATION_NODE_Z_INDEX
    //     : data.type === BlockEnum.Loop
    //       ? LOOP_NODE_Z_INDEX
    //       : zIndex,
    zIndex,
    ...rest,
  } as Node;

  return {
    newNode,
  };
}

/** @name 初始化节点 */
export const initialNodes = (originNodes: Node[], originEdges: Edge[]) => {
  const { nodes, edges } = preprocessNodesAndEdges(
    cloneDeep(originNodes),
    cloneDeep(originEdges),
  );

  const firstNode = nodes[0];

  if (!firstNode?.position) {
    nodes.forEach((node, index) => {
      node.position = {
        x: START_INITIAL_POSITION.x + index * NODE_WIDTH_X_OFFSET,
        y: START_INITIAL_POSITION.y,
      };
    });
  }

  return nodes.map((node) => {
    if (!node.type) node.type = CUSTOM_NODE;

    const connectedEdges = getConnectedEdges([node], edges);
    node.data._connectedSourceHandleIds = connectedEdges
      .filter((edge) => edge.source === node.id)
      .map((edge) => edge.sourceHandle || 'source');
    node.data._connectedTargetHandleIds = connectedEdges
      .filter((edge) => edge.target === node.id)
      .map((edge) => edge.targetHandle || 'target');

    return node;
  });
};

/** @name 初始化获取到的边信息 */
export const initialEdges = (originEdges: Edge[], originNodes: Node[]) => {
  const { nodes, edges } = preprocessNodesAndEdges(
    cloneDeep(originNodes),
    cloneDeep(originEdges),
  );
  let selectedNode: Node | null = null;
  const nodesMap = nodes.reduce(
    (acc, node) => {
      acc[node.id] = node;

      if (node.data?.selected) selectedNode = node;

      return acc;
    },
    {} as Record<string, Node>,
  );

  const cycleEdges = getCycleEdges(nodes, edges);
  return edges
    .filter((edge) => {
      return !cycleEdges.find(
        (cycEdge: any) =>
          cycEdge.source === edge.source && cycEdge.target === edge.target,
      );
    })
    .map((edge) => {
      edge.type = 'custom';

      if (!edge.sourceHandle) edge.sourceHandle = 'source';

      if (!edge.targetHandle) edge.targetHandle = 'target';

      if (!edge.data?.sourceType && edge.source && nodesMap[edge.source]) {
        edge.data = {
          ...edge.data,
          sourceType: nodesMap[edge.source].data.type!,
        } as any;
      }

      if (!edge.data?.targetType && edge.target && nodesMap[edge.target]) {
        edge.data = {
          ...edge.data,
          targetType: nodesMap[edge.target].data.type!,
        } as any;
      }

      if (selectedNode) {
        edge.data = {
          ...edge.data,
          _connectedNodeIsSelected:
            edge.source === selectedNode.id || edge.target === selectedNode.id,
        } as any;
      }
      edge.markerEnd = {
        type: MarkerType.ArrowClosed,
        width: 12,
        height: 12,
        color: "#296DFF",
        orient: "auto",
      };
      return edge;
    });
};

/** @name 根据节点当前状态获取边颜色 */
export const getEdgeColor = (
  nodeRunningStatus?: NodeRunningStatus,
  isFailBranch?: boolean,
) => {
  if (nodeRunningStatus === NodeRunningStatus.Succeeded)
    return 'var(--color-workflow-link-line-success-handle)';

  if (nodeRunningStatus === NodeRunningStatus.Failed)
    return 'var(--color-workflow-link-line-error-handle)';

  if (nodeRunningStatus === NodeRunningStatus.Exception)
    return 'var(--color-workflow-link-line-failure-handle)';

  if (nodeRunningStatus === NodeRunningStatus.Running) {
    if (isFailBranch) return 'var(--color-workflow-link-line-failure-handle)';

    return 'var(--color-workflow-link-line-handle)';
  }

  return 'var(--color-workflow-link-line-normal)';
};

type ConnectedSourceOrTargetNodesChange = {
  type: string;
  edge: Edge;
}[];
export const getNodesConnectedSourceOrTargetHandleIdsMap = (
  changes: ConnectedSourceOrTargetNodesChange,
  nodes: Node[],
) => {
  const nodesConnectedSourceOrTargetHandleIdsMap = {} as Record<string, any>;

  changes.forEach((change) => {
    const { edge, type } = change;
    const sourceNode = nodes.find((node) => node.id === edge.source)!;
    if (sourceNode) {
      nodesConnectedSourceOrTargetHandleIdsMap[sourceNode.id] =
        nodesConnectedSourceOrTargetHandleIdsMap[sourceNode.id] || {
          _connectedSourceHandleIds: [
            ...(sourceNode?.data._connectedSourceHandleIds || []),
          ],
          _connectedTargetHandleIds: [
            ...(sourceNode?.data._connectedTargetHandleIds || []),
          ],
        };
    }

    const targetNode = nodes.find((node) => node.id === edge.target)!;
    if (targetNode) {
      nodesConnectedSourceOrTargetHandleIdsMap[targetNode.id] =
        nodesConnectedSourceOrTargetHandleIdsMap[targetNode.id] || {
          _connectedSourceHandleIds: [
            ...(targetNode?.data._connectedSourceHandleIds || []),
          ],
          _connectedTargetHandleIds: [
            ...(targetNode?.data._connectedTargetHandleIds || []),
          ],
        };
    }

    if (sourceNode) {
      if (type === 'remove') {
        const index = nodesConnectedSourceOrTargetHandleIdsMap[
          sourceNode.id
        ]._connectedSourceHandleIds.findIndex(
          (handleId: string) => handleId === edge.sourceHandle,
        );
        nodesConnectedSourceOrTargetHandleIdsMap[
          sourceNode.id
        ]._connectedSourceHandleIds.splice(index, 1);
      }

      if (type === 'add')
        nodesConnectedSourceOrTargetHandleIdsMap[
          sourceNode.id
        ]._connectedSourceHandleIds.push(edge.sourceHandle || 'source');
    }

    if (targetNode) {
      if (type === 'remove') {
        const index = nodesConnectedSourceOrTargetHandleIdsMap[
          targetNode.id
        ]._connectedTargetHandleIds.findIndex(
          (handleId: string) => handleId === edge.targetHandle,
        );
        nodesConnectedSourceOrTargetHandleIdsMap[
          targetNode.id
        ]._connectedTargetHandleIds.splice(index, 1);
      }

      if (type === 'add')
        nodesConnectedSourceOrTargetHandleIdsMap[
          targetNode.id
        ]._connectedTargetHandleIds.push(edge.targetHandle || 'target');
    }
  });

  return nodesConnectedSourceOrTargetHandleIdsMap;
};

/** @name 获取节点的并行信息 */
export const getParallelInfo = (
  nodes: Node[],
  edges: Edge[],
  parentNodeId?: string,
) => {
  let startNode;

  if (parentNodeId) {
    const parentNode = nodes.find((node) => node.id === parentNodeId);
    if (!parentNode) throw new Error('Parent node not found');

    startNode = nodes.find(
      (node) =>
        node.id ===
        (parentNode.data as IterationNodeType | LoopNodeType).start_node_id,
    );
  } else {
    startNode = nodes.find((node) => node.data.type === BlockEnum.Start);
  }
  if (!startNode) throw new Error('Start node not found');

  const parallelList = [] as ParallelInfoItem[];
  const nextNodeHandles = [{ node: startNode, handle: 'source' }];
  let hasAbnormalEdges = false;

  const traverse = (firstNodeHandle: NodeHandle) => {
    const nodeEdgesSet = {} as Record<string, Set<string>>;
    const totalEdgesSet = new Set<string>();
    const nextHandles = [firstNodeHandle];
    const streamInfo = {} as Record<string, NodeStreamInfo>;
    const parallelListItem = {
      parallelNodeId: '',
      depth: 0,
    } as ParallelInfoItem;
    const nodeParallelInfoMap = {} as Record<string, NodeParallelInfo>;
    nodeParallelInfoMap[firstNodeHandle.node.id] = {
      parallelNodeId: '',
      edgeHandleId: '',
      depth: 0,
    };

    while (nextHandles.length) {
      const currentNodeHandle = nextHandles.shift()!;
      const { node: currentNode, handle: currentHandle = 'source' } =
        currentNodeHandle;
      const currentNodeHandleKey = currentNode.id;
      const connectedEdges = edges.filter(
        (edge) =>
          edge.source === currentNode.id && edge.sourceHandle === currentHandle,
      );
      const connectedEdgesLength = connectedEdges.length;
      const outgoers = nodes.filter((node) =>
        connectedEdges.some((edge) => edge.target === node.id),
      );
      const incomers = getIncomers(currentNode, nodes, edges);

      if (!streamInfo[currentNodeHandleKey]) {
        streamInfo[currentNodeHandleKey] = {
          upstreamNodes: new Set<string>(),
          downstreamEdges: new Set<string>(),
        };
      }

      if (nodeEdgesSet[currentNodeHandleKey]?.size > 0 && incomers.length > 1) {
        const newSet = new Set<string>();
        for (const item of totalEdgesSet) {
          if (!streamInfo[currentNodeHandleKey].downstreamEdges.has(item))
            newSet.add(item);
        }
        if (isEqual(nodeEdgesSet[currentNodeHandleKey], newSet)) {
          parallelListItem.depth = nodeParallelInfoMap[currentNode.id].depth;
          nextNodeHandles.push({ node: currentNode, handle: currentHandle });
          break;
        }
      }

      if (nodeParallelInfoMap[currentNode.id].depth > parallelListItem.depth)
        parallelListItem.depth = nodeParallelInfoMap[currentNode.id].depth;

      outgoers.forEach((outgoer) => {
        const outgoerConnectedEdges = getConnectedEdges(
          [outgoer],
          edges,
        ).filter((edge) => edge.source === outgoer.id);
        const sourceEdgesGroup = groupBy(outgoerConnectedEdges, 'sourceHandle');
        const incomers = getIncomers(outgoer, nodes, edges);

        if (outgoers.length > 1 && incomers.length > 1) hasAbnormalEdges = true;

        Object.keys(sourceEdgesGroup).forEach((sourceHandle) => {
          nextHandles.push({ node: outgoer, handle: sourceHandle });
        });
        if (!outgoerConnectedEdges.length)
          nextHandles.push({ node: outgoer, handle: 'source' });

        const outgoerKey = outgoer.id;
        if (!nodeEdgesSet[outgoerKey])
          nodeEdgesSet[outgoerKey] = new Set<string>();

        if (nodeEdgesSet[currentNodeHandleKey]) {
          for (const item of nodeEdgesSet[currentNodeHandleKey])
            nodeEdgesSet[outgoerKey].add(item);
        }

        if (!streamInfo[outgoerKey]) {
          streamInfo[outgoerKey] = {
            upstreamNodes: new Set<string>(),
            downstreamEdges: new Set<string>(),
          };
        }

        if (!nodeParallelInfoMap[outgoer.id]) {
          nodeParallelInfoMap[outgoer.id] = {
            ...nodeParallelInfoMap[currentNode.id],
          };
        }

        if (connectedEdgesLength > 1) {
          const edge = connectedEdges.find(
            (edge) => edge.target === outgoer.id,
          )!;
          nodeEdgesSet[outgoerKey].add(edge.id);
          totalEdgesSet.add(edge.id);

          streamInfo[currentNodeHandleKey].downstreamEdges.add(edge.id);
          streamInfo[outgoerKey].upstreamNodes.add(currentNodeHandleKey);

          for (const item of streamInfo[currentNodeHandleKey].upstreamNodes)
            streamInfo[item].downstreamEdges.add(edge.id);

          if (!parallelListItem.parallelNodeId)
            parallelListItem.parallelNodeId = currentNode.id;

          const prevDepth = nodeParallelInfoMap[currentNode.id].depth + 1;
          const currentDepth = nodeParallelInfoMap[outgoer.id].depth;

          nodeParallelInfoMap[outgoer.id].depth = Math.max(
            prevDepth,
            currentDepth,
          );
        } else {
          for (const item of streamInfo[currentNodeHandleKey].upstreamNodes)
            streamInfo[outgoerKey].upstreamNodes.add(item);

          nodeParallelInfoMap[outgoer.id].depth =
            nodeParallelInfoMap[currentNode.id].depth;
        }
      });
    }

    parallelList.push(parallelListItem);
  };

  while (nextNodeHandles.length) {
    const nodeHandle = nextNodeHandles.shift()!;
    traverse(nodeHandle);
  }

  return {
    parallelList,
    hasAbnormalEdges,
  };
};

export const canRunBySingle = (nodeType: BlockEnum) => {
  return false;
};

/** @description 取节点数组中左上角节点的位置 */
export const getTopLeftNodePosition = (nodes: Node[]) => {
  let minX = Infinity;
  let minY = Infinity;

  nodes.forEach((node) => {
    if (node.position.x < minX) minX = node.position.x;

    if (node.position.y < minY) minY = node.position.y;
  });

  return {
    x: minX,
    y: minY,
  };
};

/** @name 根据旧的节点标题生成新的节点标题 */
export const genNewNodeTitleFromOld = (oldTitle: string) => {
  const regex = /^(.+?)\s*\((\d+)\)\s*$/;
  const match = oldTitle.match(regex);

  if (match) {
    const title = match[1];
    const num = Number.parseInt(match[2], 10);
    return `${title} (${num + 1})`;
  } else {
    return `${oldTitle} (1)`;
  }
};

export const getLayoutByDagre = (originNodes: Node[], originEdges: Edge[]) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  const nodes = cloneDeep(originNodes).filter(
    (node) => !node.parentId && node.type === CUSTOM_NODE,
  );
  const edges = cloneDeep(originEdges).filter(
    (edge) => !edge.data?.isInIteration && !edge.data?.isInLoop,
  );
  dagreGraph.setGraph({
    rankdir: 'LR',
    align: 'UL',
    nodesep: 40,
    ranksep: 60,
    ranker: 'tight-tree',
    marginx: 30,
    marginy: 200,
  });
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: node.width!,
      height: node.height!,
    });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  return dagreGraph;
};

const specialKeysCodeMap: Record<string, string | undefined> = {
  ctrl: 'meta',
};
/** @name 根据系统类型获取键盘按键对应的键值 */
export const getKeyboardKeyCodeBySystem = (key: string) => {
  if (isMac()) return specialKeysCodeMap[key] || key;

  return key;
};

/** @name 判断事件是否在输入区域 */
export const isEventTargetInputArea = (target: HTMLElement) => {
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return true;

  if (target.contentEditable === 'true') return true;
};

/** @name 判断节点是否是错误处理节点 */
export const hasErrorHandleNode = (nodeType?: BlockEnum) => {
  return false;
  // return nodeType === BlockEnum.LLM || nodeType === BlockEnum.Tool || nodeType === BlockEnum.HttpRequest || nodeType === BlockEnum.Code
};

/** @name 判断节点是否是错误处理节点 */
export const isExceptionVariable = (variable: string, nodeType?: BlockEnum) => {
  if (
    (variable === 'error_message' || variable === 'error_type') &&
    hasErrorHandleNode(nodeType)
  )
    return true;

  return false;
};

// 将字节转换为 B/KB/MB/GB/TB 等常用单位
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const base = 1024;
  const exponent = Math.floor(Math.log(bytes) / Math.log(base));
  const size = (bytes / Math.pow(base, exponent)).toFixed(2); // 保留两位小数
  return `${parseFloat(size)} ${units[exponent]}`; // 去掉多余的零（如 1.00 → 1）
};

//转换xml为flow
export const parseXMLToFlow = (xmlString) => {
  const options = {
    ignoreAttributes: false
  };

  const xmlParser = new XMLParser(options);
  const result = xmlParser.parse(xmlString);

  const reactFlowElement = result.ReactFlow;

  // 解析 nodes
  const nodes = reactFlowElement.find(e => e.name === 'nodes').elements
    .map(nodeElement => ({
      id: `${nodeElement.attributes.id}`,
      width: Number(nodeElement.attributes.width),
      height: Number(nodeElement.attributes.height),
      type: nodeElement.attributes.type || null,
      position: {
        x: parseFloat(nodeElement.attributes.x),
        y: parseFloat(nodeElement.attributes.y)
      },
      sourcePosition: nodeElement.attributes.sourcePosition || 'right',
      targetPosition: nodeElement.attributes.targetPosition || 'left',
      data: nodeElement.attributes.data ? JSON.parse(nodeElement.attributes.data) : {}
    }));

  // 解析 edges
  const edges = reactFlowElement.find(e => e.name === 'edges').elements
    .map(edgeElement => ({
      id: edgeElement.attributes.id,
      selected: false,
      source: `${edgeElement.attributes.source}`,
      sourceHandle: edgeElement.attributes.sourceHandle,
      target: `${edgeElement.attributes.target}`,
      targetHandle: edgeElement.attributes.targetHandle,
      zIndex: edgeElement.attributes.zIndex || 0,
      type: edgeElement.attributes.type,
      data: edgeElement.attributes.data ? JSON.parse(edgeElement.attributes.data) : {}
    }));


  // 解析viewport
  const viewportAttr = reactFlowElement.find(e => e.name === 'viewport').attributes;

  let viewport = {};
  Object.keys(viewportAttr).forEach(key => {
    viewport[key] = Number(viewportAttr[key])
  })
  return { nodes, edges, viewport };
};



// 将 ReactFlow JSON 转换为 XML
export const reactFlowJsonToXml = (reactFlowJson) => {
  const elements = [];
  // 转换 nodes
  const nodesElement = {
    type: 'element',
    name: 'nodes',
    elements: reactFlowJson.nodes.map(node => {
      const { id, type = '', position, data, width, height, positionAbsolute, sourcePosition, targetPosition } = node;
      return {
        type: 'element',
        name: 'node',
        attributes: {
          id,
          type,
          width: width.toString(),
          height: height.toString(),
          x: position.x.toString(),
          y: position.y.toString(),
          sourcePosition,
          targetPosition,
          data: JSON.stringify(data),
        },
      }
    })
  };

  // 转换 edges
  const edgesElement = {
    type: 'element',
    name: 'edges',
    elements: reactFlowJson.edges.map(edge => {
      const { id, selected, source, sourceHandle, target, targetHandle, type, zIndex, data } = edge;
      return {
        type: 'element',
        name: 'edge',
        attributes: {
          id,
          selected,
          source,
          sourceHandle,
          target,
          targetHandle,
          type,
          zIndex,
          data: JSON.stringify(data)
        }
      }
    })
  };

  const viewportElement = {
    type: 'element',
    name: 'viewport',
    attributes: {
      x: reactFlowJson.viewport.x.toFixed(2),
      y: reactFlowJson.viewport.y.toFixed(2),
      zoom: reactFlowJson.viewport.zoom.toFixed(2),
    },
  };

  elements.push(nodesElement, edgesElement, viewportElement);


  const options = {
    ignoreAttributes: false,
    arrayNodeName: "ReactFlow"
  };


  const xmlBuilder = new XMLBuilder(options);

  const xml = xmlBuilder.build(elements);

  return xml;
}

// AI生成节点-整理节点
const handleLayoutNodes = (nodes, edges) => {
  const layout = getLayoutByDagre(nodes, edges);
  const rankMap = {} as Record<string, Node>;

  nodes.forEach((node) => {
    if (!node.parentId && node.type === CUSTOM_NODE) {
      const rank = layout.node(node.id).rank!;

      if (!rankMap[rank]) {
        rankMap[rank] = node;
      } else {
        if (rankMap[rank].position.y > node.position.y) rankMap[rank] = node;
      }
    }
  });

  const newNodes = produce(nodes, (draft) => {
    draft.forEach((node) => {
      if (!node.parentId && node.type === CUSTOM_NODE) {
        const nodeWithPosition = layout.node(node.id);

        node.position = {
          x: nodeWithPosition.x - node.width! / 2,
          y:
            nodeWithPosition.y -
            node.height! / 2 +
            rankMap[nodeWithPosition.rank!].height! / 2,
        };
      }
    });
  });
  return newNodes;
};

// 将AI处理后的数组转为reactFlowJson
const convertStepsTOJson = (steps) => {
  const nodes = [];
  const edges = [];
  let existingIds = new Set(); // 用于存储已存在的ID，确保唯一性
  let nodeIdMapId = new Map();
  let hashCounter = 1; // 用于assignment-online节点的hash值递增

  // 生成唯一9位数字ID的函数
  function generateUniqueId() {
    let id;
    do {
      id = Math.floor(100000000 + Math.random() * 900000000).toString();
    } while (existingIds.has(id));
    existingIds.add(id);
    return id;
  }

  // 判断steps里首尾是否包含开始、结束，若未包含，则添加
  if (steps[0].title != '开始') {
    steps.unshift({
      type: 'start',
      title: '开始',
      nodeId: "start"
    })
  } else {
    steps[0].type = 'start'
    steps[0].nodeId = "start"
  }
  if (steps[steps.length - 1].title != '结束') {
    steps.push({
      type: 'end',
      title: '结束',
      nodeId: "end"
    })
  } else {
    steps[steps.length - 1].type = 'end'
    steps[steps.length - 1].nodeId = "end"
  }

  // 处理节点数据
  steps.forEach((step, index) => {
    // 确定节点类型和标题
    let nodeType, title;
    switch (step.type) {
      case 'start':
        nodeType = 'start';
        title = '开始'; // 覆盖原始标题
        break;
      case 'end':
        nodeType = 'end';
        title = '结束'; // 覆盖原始标题
        break;
      case 'online':
        nodeType = 'assignment-online';
        title = step.title; // 使用原始标题
        break;
      case 'offline':
        nodeType = 'assignment-offline';
        title = step.title;
        break;
      // 可以添加其他类型的处理
      default:
        nodeType = 'assignment-online';
        title = step.title;
    }

    const nodeDateId = `${Date.now()}${index}`;
    nodeIdMapId.set(step.nodeId, {id: nodeDateId, title: title, type: nodeType})
    // 构建基础节点对象
    const node: any = {
      id: nodeDateId,
      type: 'custom', // 使用自定义节点类型
      width: (nodeType == "start" || nodeType == "end") ? 54 : 244,
      height: (nodeType == "start" || nodeType == "end") ? 54 : 180,
      position: {
        x: index == 0 ? 0 : (index * 300 - 190), // 水平等距排列
        y: 300
      },
      targetPosition: 'left',
      sourcePosition: 'right',
      data: {
        type: nodeType,
        title: title,
      }
    };

    // 为assignment-online类型添加额外数据
    if (nodeType === 'assignment-online' || nodeType === 'assignment-offline') {
      node.data = {
        ...node.data,
        hash: hashCounter++,
        crudDatas: step.crudDatas ? step.crudDatas.map(item => ({ ...item, id: uuid4() })) : [],
        detail: {
          id: generateUniqueId(), // 生成唯一的detail ID
          title: step.title,
          station: step.station || "默认岗位", // 示例数据
          businessTags: ['主场景'],
          desc: step.desc || undefined,
          bindBusinessScenario: {
            id: "",
            name: ""
          }
        }
      };
    }

    nodes.push(node);
    if (step.branch && step.branch.length) {
      step.branch.forEach((k, kndex) => {
        const branchToId = k.to;
        // 如果是连接前面的node，则需要创建mirror节点
        if (nodeIdMapId.has(branchToId)) {
          const nodeIdMapData = nodeIdMapId.get(branchToId);
          nodeIdMapId.set(`${branchToId}Mirror`, {id: `${branchToId}Mirror`, title: '镜像节点', type: "mirror"})
          k.to = `${branchToId}Mirror`;
          const mirrorNode = {
            id: `${branchToId}Mirror`,
            type: 'custom', // 使用自定义节点类型
            position: {
              x: index == 0 ? 0 : (index * 300 - 190), // 水平等距排列
              y: 600
            },
            targetPosition: 'left',
            data: {
              type: "mirror",
              mirrorNode: {id: nodeIdMapData.id, name: nodeIdMapData.title}
            }
          }
          nodes.push(mirrorNode);
        }
      })
    }
  });

  // 创建连接线（排除最后一个节点）
  for (let i = 0; i < steps.length - 1; i++) {
    const sourceNode = steps[i];
    const sourceNodeData = nodeIdMapId.get(sourceNode.nodeId);
    const branch = sourceNode.branch;
    if(branch && branch.length > 0) {
      branch.forEach(k => {
        const targetNodeData = nodeIdMapId.get(k.to);
        edges.push({
          id: `${sourceNodeData.id}-source-${targetNodeData.id}-target`,
          type: 'custom',
          source: sourceNodeData.id,
          sourceHandle: 'source',
          target: targetNodeData.id,
          targetHandle: 'target',
          data: {
            label: k.label,
            sourceType: sourceNodeData.type,
            targetType: targetNodeData.type
          }
        });
      });
    } else {
      const targetNode = steps[i + 1];
      const targetNodeData = nodeIdMapId.get(targetNode.nodeId);
      edges.push({
        id: `${sourceNodeData.id}-source-${targetNodeData.id}-target`,
        type: 'custom',
        source: sourceNodeData.id,
        sourceHandle: 'source',
        target: targetNodeData.id,
        targetHandle: 'target',
        data: {
          sourceType: sourceNodeData.type,
          targetType: targetNodeData.type
        }
      });
    }
    

    
  }

  const newNodes = handleLayoutNodes(nodes, edges)

  return { nodes: newNodes, edges, viewport: { x: 100, y: 0, zoom: 1 } };
}
export const convertWfAndImport = async (aiData) => {
  const http = useHTTP();
  const { aiOutput, prjId, sVer = 1, pmId } = aiData;

  // 移除think部分（think可能出现在响应中的任何位置）
  const withoutThink = aiOutput.replace(/think:\s*{[^}]*}/gi, '');

  // 查找markdown JSON代码块（支持```json和```）
  const jsonBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/;
  const match = withoutThink.match(jsonBlockRegex);

  if (match && match[1]) {
    const wfListsJson = JSON.parse(match[match.length - 1].trim());
    const obj = parseAiBf(wfListsJson);
    const wfLists = obj.wfLists;

    const results = await Promise.all(
      wfLists.map(async (item, wfIndex) => {
        try {
          const jsonData = item;
          if (!jsonData.wfName) {
            throw new Error("请在描述中给出流程名称，例如： 款式开发流程如下");
          }
          const steps = jsonData?.wfArr || [];
          if (!steps.length) {
            throw new Error(
              "请在描述中给出流程步骤，例如： 1. 业务岗编制款式档案 2. 业务岗编制款式资料 3.业务岗编制打样通知单 4. 打样中心开始打样"
            );
          }
          const graph = convertStepsTOJson(steps);

          const params = {
            prjId,
            sVer,
            pmId,
            bfNo: jsonData.wfCode,
            bfName: jsonData.wfName,
            bfType: jsonData.wfType == "sop" ? "TARGET" : "TARGET2",
            wfJson: JSON.stringify({ graph }),
          };

          const data = await runAsync(
            async () => {
              return await http.post("/forward/importBfWfModuleForAi", params);
            },
            {
              action: `导入第${wfIndex + 1}个业务流程`,
              error: { content: (err) => `导入第${wfIndex + 1}个业务流程失败: ${err.message}` },
              loading: { show: false },
              success: { show: true },
            }
          );

          return { bfId: data.bfId, success: true, index: wfIndex, bfName: data.bfName };
        } catch (e) {
          console.error(`第${wfIndex + 1}个业务流程处理失败:`, e);
          return { success: false, error: e.message, index: wfIndex };
        }
      })
    )

    // 返回最后一个成功的结果
    const successfulResults = results.filter(result => result.success);
    if (successfulResults.length > 0) {
      return successfulResults[successfulResults.length - 1];
    } else {
      throw new Error("所有业务流程导入都失败了");
    }

  } else {
    throw new Error("未找到Markdown JSON代码块");
  }

  // const jsonString = aiOutput.includes('</think>') ? aiOutput.split('</think>')[1].split('===JSON_START===')[1].split('===JSON_END===')[0].trim() : aiOutput.split('===JSON_START===')[1].split('===JSON_END===')[0].trim();
  // const jsonData = JSON.parse(jsonString);
};

/**
 * 处理多源节点的拓扑排序
 * 支持分支、合并等复杂结构
 */
export const sortNodesWithMultipleSources = (nodes, edges) => {
  // 创建节点映射
  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  
  // 构建邻接表和入度表
  const adjacencyList = new Map();  // 出边：source -> [targets]
  const reverseAdjacencyList = new Map(); // 入边：target -> [sources]
  const inDegree = new Map(); // 入度计数
  
  // 初始化数据结构
  nodes.forEach(node => {
    adjacencyList.set(node.id, []);
    reverseAdjacencyList.set(node.id, []);
    inDegree.set(node.id, 0);
  });
  
  // 构建图
  edges.forEach(edge => {
    const { source, target } = edge;
    
    // 确保节点存在
    if (nodeMap.has(source) && nodeMap.has(target)) {
      // 添加到邻接表
      adjacencyList.get(source).push(target);
      
      // 添加到反向邻接表
      reverseAdjacencyList.get(target).push(source);
      
      // 增加入度
      inDegree.set(target, inDegree.get(target) + 1);
    }
  });
  
  // 找出所有入度为0的起始节点
  const startNodes = [];
  for (const [nodeId, degree] of inDegree) {
    if (degree === 0) {
      startNodes.push(nodeId);
    }
  }
  
  // 如果没有起始节点（可能是环），则使用所有节点
  if (startNodes.length === 0) {
    console.warn('检测到可能的循环依赖或所有节点都有入边，将使用所有节点作为起始点');
    nodes.forEach(node => startNodes.push(node.id));
    // 重置入度
    nodes.forEach(node => inDegree.set(node.id, 0));
  }
  
  // 拓扑排序 - Kahn算法
  const sortedNodeIds = [];
  const queue = [...startNodes];
  
  // 用于检测循环依赖
  const processedCount = new Map();
  nodes.forEach(node => processedCount.set(node.id, 0));
  
  while (queue.length > 0) {
    const currentNodeId = queue.shift();
    sortedNodeIds.push(currentNodeId);
    
    // 处理当前节点的所有后继节点
    const successors = adjacencyList.get(currentNodeId) || [];
    
    successors.forEach(successorId => {
      // 减少入度
      const newDegree = inDegree.get(successorId) - 1;
      inDegree.set(successorId, newDegree);
      
      // 如果入度变为0，加入队列
      if (newDegree === 0) {
        queue.push(successorId);
      }
      
      // 记录处理次数，防止无限循环
      processedCount.set(successorId, processedCount.get(successorId) + 1);
      if (processedCount.get(successorId) > nodes.length) {
        console.error(`检测到可能的无限循环，节点 ${successorId} 被处理次数过多`);
        throw new Error('图中存在循环依赖');
      }
    });
  }
  
  // 处理可能的环或孤立节点
  const allNodeIds = nodes.map(node => node.id);
  const sortedSet = new Set(sortedNodeIds);
  const unsortedNodeIds = allNodeIds.filter(id => !sortedSet.has(id));
  
  // 如果还有未排序的节点，尝试深度优先搜索
  if (unsortedNodeIds.length > 0) {
    console.warn(`有 ${unsortedNodeIds.length} 个节点未通过拓扑排序，可能存在于环中`);
    
    // 使用DFS为剩余节点排序
    const visited = new Set();
    const dfsOrder = [];
    
    const dfs = (nodeId, path = new Set()) => {
      if (path.has(nodeId)) {
        // 检测到环
        console.warn(`检测到循环: ${[...path, nodeId].join(' -> ')}`);
        return;
      }
      
      if (visited.has(nodeId)) return;
      
      visited.add(nodeId);
      path.add(nodeId);
      
      // 先处理所有前驱（依赖）
      const predecessors = reverseAdjacencyList.get(nodeId) || [];
      predecessors.forEach(predId => {
        if (allNodeIds.includes(predId) && !visited.has(predId)) {
          dfs(predId, new Set(path));
        }
      });
      
      dfsOrder.push(nodeId);
    };
    
    unsortedNodeIds.forEach(nodeId => {
      if (!visited.has(nodeId)) {
        dfs(nodeId);
      }
    });
    
    // 将DFS结果添加到排序列表
    dfsOrder.forEach(nodeId => {
      if (!sortedSet.has(nodeId)) {
        sortedNodeIds.push(nodeId);
      }
    });
  }
  
  // 按排序后的ID顺序重建节点数组
  const sortedNodes = [];
  const addedIds = new Set();
  
  sortedNodeIds.forEach(nodeId => {
    const node = nodeMap.get(nodeId);
    if (node && !addedIds.has(nodeId)) {
      sortedNodes.push(node);
      addedIds.add(nodeId);
    }
  });
  
  // 确保所有节点都被包含（安全措施）
  nodes.forEach(node => {
    if (!addedIds.has(node.id)) {
      sortedNodes.push(node);
    }
  });
  
  return sortedNodes;
};